import type { FeatureEvents, ScreenEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject } from '../new-analytics-types';

type ScreenKeys =
  | 'sandboxDeploymentLogScreen'
  | 'sandboxDeploymentCreateScreen'
  | 'sandboxDeploymentScanningDataScreen'
  | 'sandboxDeploymentScanCompleteScreen'
  | 'sandboxDeploymentScanFailedScreen'
  | 'sandboxDeploymentValidationInProgressScreen'
  | 'sandboxDeploymentValidationFailedScreen'
  | 'sandboxDeploymentPendingDeploymentScreen'
  | 'sandboxDeploymentInProgressScreen'
  | 'sandboxDeploymentSuccessfulScreen'
  | 'sandboxDeploymentFailedScreen'
  | 'sandboxDeploymentIncompleteScreen'
  | 'sandboxDeploymentReportScreen';

type UiKeys =
  | 'createDeploymentButtonClicked'
  | 'viewDeploymentLogButtonClicked'
  | 'selectDestinationButtonClicked'
  | 'startReconciliationButtonClicked'
  | 'viewEntityOverviewButtonClicked'
  | 'viewDependenciesButtonClicked'
  | 'viewUsedByButtonClicked'
  | 'viewProjectsImpactedButtonClicked'
  | 'clickedSubGroupExpandedButton'
  | 'refreshReconciliationButtonClicked'
  | 'onFiltersChangedButtonClicked'
  | 'startValidationButtonClicked'
  | 'startDeploymentButtonClicked'
  | 'deleteDeploymentButtonClicked'
  | 'viewDeploymentDetailsButtonClicked'
  | 'viewDeploymentReportButtonClicked'
  | 'downloadPMRButtonClicked'
  | 'onUpdateDeploymentName';

export enum SandboxDeploymentScreens {
  sandboxDeploymentLogScreen = 'sandboxDeploymentLogScreen',
  sandboxDeploymentCreateScreen = 'sandboxDeploymentCreateScreen',
  sandboxDeploymentScanningDataScreen = 'sandboxDeploymentScanningDataScreen',
  sandboxDeploymentScanCompleteScreen = 'sandboxDeploymentScanCompleteScreen',
  sandboxDeploymentScanFailedScreen = 'sandboxDeploymentScanFailedScreen',
  sandboxDeploymentValidationInProgressScreen = 'sandboxDeploymentValidationInProgressScreen',
  sandboxDeploymentValidationFailedScreen = 'sandboxDeploymentValidationFailedScreen',
  sandboxDeploymentPendingDeploymentScreen = 'sandboxDeploymentPendingDeploymentScreen',
  sandboxDeploymentInProgressScreen = 'sandboxDeploymentInProgressScreen',
  sandboxDeploymentSuccessfulScreen = 'sandboxDeploymentSuccessfulScreen',
  sandboxDeploymentFailedScreen = 'sandboxDeploymentFailedScreen',
  sandboxDeploymentIncompleteScreen = 'sandboxDeploymentIncompleteScreen',
  sandboxDeploymentReportScreen = 'sandboxDeploymentReportScreen',
  sandboxMainDashboardScreen = 'sandboxMainDashboardScreen',
  sandboxDeploymentEntityDetailsSidePanelScreen = 'sandboxDeploymentEntityDetailsSidePanelScreen',
}

export const sandboxDeploymentEvents: FeatureEvents<ScreenKeys, UiKeys, string, string> = {
  confluenceSpec:
    'https://hello.atlassian.net/wiki/spaces/TESTIN/pages/6101015819/Specification+for+Analytics+Events+-+Sandbox+Config+Promo',

  screen: {
    sandboxDeploymentLogScreen: (attributes: { orgId: string }): ScreenEvent => ({
      data: {
        name: SandboxDeploymentScreens.sandboxDeploymentLogScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92757',
    }),

    sandboxDeploymentCreateScreen: (attributes: { orgId: string; sandboxCloudId: string }): ScreenEvent => ({
      data: {
        name: SandboxDeploymentScreens.sandboxDeploymentCreateScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92880',
    }),

    sandboxDeploymentScanningDataScreen: (attributes: { orgId: string; sandboxCloudId: string; deploymentId: string }): ScreenEvent => ({
      data: {
        name: SandboxDeploymentScreens.sandboxDeploymentScanningDataScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92881',
    }),

    sandboxDeploymentScanCompleteScreen: (attributes: { orgId: string; sandboxCloudId: string; deploymentId: string }): ScreenEvent => ({
      data: {
        name: SandboxDeploymentScreens.sandboxDeploymentScanCompleteScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92884',
    }),

    sandboxDeploymentScanFailedScreen: (attributes: { orgId: string; sandboxCloudId: string; deploymentId: string }): ScreenEvent => ({
      data: {
        name: SandboxDeploymentScreens.sandboxDeploymentScanFailedScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92882',
    }),

    sandboxDeploymentValidationInProgressScreen: (attributes: {
      orgId: string;
      sandboxCloudId: string;
      deploymentId: string;
    }): ScreenEvent => ({
      data: {
        name: SandboxDeploymentScreens.sandboxDeploymentValidationInProgressScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92883',
    }),

    sandboxDeploymentValidationFailedScreen: (attributes: {
      orgId: string;
      sandboxCloudId: string;
      deploymentId: string;
    }): ScreenEvent => ({
      data: {
        name: SandboxDeploymentScreens.sandboxDeploymentValidationFailedScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92885',
    }),

    sandboxDeploymentPendingDeploymentScreen: (attributes: {
      orgId: string;
      sandboxCloudId: string;
      deploymentId: string;
    }): ScreenEvent => ({
      data: {
        name: SandboxDeploymentScreens.sandboxDeploymentPendingDeploymentScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92886',
    }),

    sandboxDeploymentInProgressScreen: (attributes: { orgId: string; sandboxCloudId: string; deploymentId: string }): ScreenEvent => ({
      data: {
        name: SandboxDeploymentScreens.sandboxDeploymentInProgressScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92887',
    }),

    sandboxDeploymentSuccessfulScreen: (attributes: { orgId: string; sandboxCloudId: string; deploymentId: string }): ScreenEvent => ({
      data: {
        name: SandboxDeploymentScreens.sandboxDeploymentSuccessfulScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92888',
    }),

    sandboxDeploymentFailedScreen: (attributes: { orgId: string; sandboxCloudId: string; deploymentId: string }): ScreenEvent => ({
      data: {
        name: SandboxDeploymentScreens.sandboxDeploymentFailedScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92889',
    }),

    sandboxDeploymentIncompleteScreen: (attributes: { orgId: string; sandboxCloudId: string; deploymentId: string }): ScreenEvent => ({
      data: {
        name: SandboxDeploymentScreens.sandboxDeploymentIncompleteScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92890',
    }),

    sandboxDeploymentReportScreen: (attributes: { orgId: string; sandboxCloudId: string; deploymentId: string }): ScreenEvent => ({
      data: {
        name: SandboxDeploymentScreens.sandboxDeploymentReportScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/92892',
    }),
  },
  ui: {
    createDeploymentButtonClicked: (attributes: { orgId: string; sandboxCloudId: string; source: SandboxDeploymentScreens }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'createDeploymentButton',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93172',
    }),

    viewDeploymentLogButtonClicked: (attributes: { orgId: string; sandboxCloudId: string; source: SandboxDeploymentScreens }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'viewDeploymentLogButton',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93173',
    }),

    selectDestinationButtonClicked: (attributes: { orgId: string; sandboxCloudId: string; source: SandboxDeploymentScreens }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'selectDestinationButton',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93174',
    }),

    startReconciliationButtonClicked: (attributes: {
      orgId: string;
      sandboxCloudId: string;
      deploymentId: string;
      source: SandboxDeploymentScreens;
      isProd: boolean;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'startReconciliationButton',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93175',
    }),

    viewEntityOverviewButtonClicked: (attributes: {
      orgId: string;
      sandboxCloudId: string;
      deploymentId: string;
      source: SandboxDeploymentScreens;
      entityName: string;
      entityId: string;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'viewEntityOverviewButton',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93176',
    }),

    viewDependenciesButtonClicked: (attributes: {
      orgId: string;
      sandboxCloudId: string;
      deploymentId: string;
      source: SandboxDeploymentScreens;
      entityName: string;
      entityId: string;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'viewDependenciesButton',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93177',
    }),

    viewUsedByButtonClicked: (attributes: {
      orgId: string;
      sandboxCloudId: string;
      deploymentId: string;
      source: SandboxDeploymentScreens;
      entityName: string;
      entityId: string;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'viewUsedByButton',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93179',
    }),

    viewProjectsImpactedButtonClicked: (attributes: {
      orgId: string;
      sandboxCloudId: string;
      deploymentId: string;
      source: SandboxDeploymentScreens;
      entityName: string;
      entityId: string;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'viewProjectsButton',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93180',
    }),

    clickedSubGroupExpandedButton: (attributes: {
      orgId: string;
      sandboxCloudId: string;
      deploymentId: string;
      source: SandboxDeploymentScreens;
      subGroupName: string;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'clickedSubGroupExpandedButton',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93181',
    }),

    refreshReconciliationButtonClicked: (attributes: {
      orgId: string;
      sandboxCloudId: string;
      deploymentId: string;
      source: SandboxDeploymentScreens;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'refreshReconciliationButton',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93182',
    }),

    onFiltersChangedButtonClicked: (attributes: {
      orgId: string;
      sandboxCloudId: string;
      deploymentId: string;
      source: SandboxDeploymentScreens;
      filterName: string;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'onFiltersChangedButton',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93183',
    }),

    startValidationButtonClicked: (attributes: {
      orgId: string;
      sandboxCloudId: string;
      deploymentId: string;
      source: SandboxDeploymentScreens;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'startValidationButton',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93184',
    }),

    startDeploymentButtonClicked: (attributes: {
      orgId: string;
      sandboxCloudId: string;
      deploymentId: string;
      source: SandboxDeploymentScreens;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'startDeploymentButton',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93185',
    }),

    deleteDeploymentButtonClicked: (attributes: {
      orgId: string;
      sandboxCloudId: string;
      deploymentId: string;
      source: SandboxDeploymentScreens;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'deleteDeploymentButton',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93186',
    }),

    viewDeploymentDetailsButtonClicked: (attributes: {
      orgId: string;
      sandboxCloudId: string;
      deploymentId: string;
      source: SandboxDeploymentScreens;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'viewDeploymentDetailsButton',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93187',
    }),

    viewDeploymentReportButtonClicked: (attributes: {
      orgId: string;
      sandboxCloudId: string;
      deploymentId: string;
      source: SandboxDeploymentScreens;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'viewDeploymentReportButton',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93188',
    }),

    downloadPMRButtonClicked: (attributes: { orgId: string; sandboxCloudId: string; deploymentId: string; source: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'downloadPMRButton',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93189',
    }),

    onUpdateDeploymentName: (attributes: { orgId: string; sandboxCloudId: string; deploymentId: string; source: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubject: ActionSubject.Button,
        actionSubjectId: 'changDeploymentNameButton',
        source: attributes.source,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/93190',
    }),
  },
  track: {},
  operational: {},
};
